import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { sandbox } from "../api/client";
import { sandboxEnabled, useSession } from "../auth/session";
import { fmt, t, stateLabel } from "../i18n";
import { errorMessage, useToast } from "./ui";

interface SandboxCtx {
  /** pedido aberto na tela atual */
  order: { id: string; state: string; totalCents: string } | null;
  setOrder: (o: { id: string; state: string; totalCents: string } | null) => void;
  /** chamado depois de uma simulação, para a tela recarregar */
  onSimulated: (() => void) | null;
  setOnSimulated: (fn: (() => void) | null) => void;
}
const Ctx = createContext<SandboxCtx>({ order: null, setOrder: () => undefined, onSimulated: null, setOnSimulated: () => undefined });

export function SandboxProvider({ children }: { children: ReactNode }) {
  const [order, setOrder] = useState<SandboxCtx["order"]>(null);
  const [onSimulated, setCb] = useState<(() => void) | null>(null);
  const setOnSimulated = useCallback((fn: (() => void) | null) => setCb(() => fn), []);
  const value = useMemo(() => ({ order, setOrder, onSimulated, setOnSimulated }), [order, onSimulated, setOnSimulated]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSandbox() {
  return useContext(Ctx);
}

export function SandboxPanel() {
  const { me } = useSession();
  const { order, onSimulated } = useSandbox();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // começa recolhido (só o título) para nunca cobrir botões da tela; lembra a escolha na aba
  const [open, setOpenState] = useState<boolean>(() => { try { return sessionStorage.getItem("fxp.sandbox.open") === "1"; } catch { return false; } });
  const setOpen = (fn: (v: boolean) => boolean) => setOpenState((v) => { const n = fn(v); try { sessionStorage.setItem("fxp.sandbox.open", n ? "1" : "0"); } catch { /* sem storage */ } return n; });
  if (!sandboxEnabled(me)) return null;

  const ready = Boolean(order && order.state === "awaiting_funds") && !busy;

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      toast(t.sandbox.done);
      onSimulated?.();
    } catch (e) {
      toast(fmt(t.sandbox.failed, { msg: errorMessage(e) }));
    } finally {
      setBusy(false);
    }
  }

  function divergentCents(total: string): string {
    const n = BigInt(total);
    const d = n > 100n ? n - 60n : n + 60n;
    return d.toString();
  }

  return (
    <aside className="simbar" aria-label={t.sandbox.title}>
      <div className="row between" style={{ gap: 8 }}>
        <span className="lbl">{t.sandbox.title}</span>
        <button type="button" className="toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-label={open ? "Recolher demonstração" : "Abrir demonstração"}>
          {open ? "−" : "+"}
        </button>
      </div>
      {open && (
        <>
          <span className="st">
            {order ? (
              <>
                {order.id} · {fmt(t.sandbox.state, { state: "" })}
                <b>{stateLabel(order.state)}</b>
              </>
            ) : (
              t.sandbox.noOrder
            )}
          </span>
          <button type="button" disabled={!ready} onClick={() => order && run(() => sandbox.pixPay(order.id))}>
            {t.sandbox.payExact}
          </button>
          <button type="button" disabled={!ready} onClick={() => order && run(() => sandbox.pixPay(order.id, divergentCents(order.totalCents)))}>
            {t.sandbox.payDivergent}
          </button>
          <button type="button" disabled={!ready} onClick={() => order && run(() => sandbox.pixExpire(order.id))}>
            {t.sandbox.expire}
          </button>
          {busy && <span className="st">{t.sandbox.working}</span>}
        </>
      )}
    </aside>
  );
}
